import { Injectable, NgModule } from '@angular/core';
import { PreloadingStrategy, Route, Routes, RouterModule } from '@angular/router';
import { catchError, Observable, of } from 'rxjs';
import { MsalGuard } from '@azure/msal-angular';
import { HomeComponent } from './components/home';
import { OrganizationComponent } from './components/organization/organization.component';
import { OrganizationOverviewComponent } from './components/organization/organization-overview/organization-overview.component';
import { CurrentActivitiesComponent } from './components/organization/activities/current-activities/current-activities.component';

// Only the pages you land on after login (start page, organization overview, current activities) are in the initial
// bundle. Everything else is loaded on first navigation; see NewVersionService for when such a chunk is gone after a deploy.
/**
 * Preloads lazy routes marked with `data: { preload: true }` in the background once the first page is shown, so the next
 * click doesn't wait for the download. A failed preload (e.g. a chunk gone after a deploy) is ignored here; the real
 * navigation then shows NewVersionService's dialog.
 */
@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    return route.data?.['preload'] ? load().pipe(catchError(() => of(null))) : of(null);
  }
}

const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'organization/register',
    loadComponent: () => import('./components/organization/register/register.component').then(m => m.RegisterComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'organization/search',
    loadComponent: () => import('./components/organization/search/search.component').then(m => m.SearchComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'profile',
    loadComponent: () =>
      import('./components/profile/profile-settings/profile-settings.component').then(m => m.ProfileSettingsComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'admin',
    loadChildren: () => import('./components/admin/admin.routes').then(m => m.ADMIN_ROUTES),
    canActivate: [MsalGuard],
  },
  {
    path: 'organization/:id',
    component: OrganizationComponent,
    canActivate: [MsalGuard],
    children: [
      {
        path: '',
        component: OrganizationOverviewComponent,
      },
      {
        path: 'to-be-removed',
        loadComponent: () =>
          import('./components/organization/to-be-removed/to-be-removed.component').then(m => m.ToBeRemovedComponent),
      },
      {
        path: 'activities/create',
        loadComponent: () =>
          import('./components/organization/activities/create-activity/create-activity.component').then(
            m => m.CreateActivityComponent
          ),
      },
      {
        path: 'settings',
        loadComponent: () => import('./components/organization/settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'members',
        loadChildren: () => import('./components/organization/members/members.routes').then(m => m.MEMBERS_ROUTES),
      },
      {
        path: 'current',
        component: CurrentActivitiesComponent,
      },
      {
        path: 'archived',
        loadComponent: () =>
          import('./components/organization/activities/archived-activities/archived-activities.component').then(
            m => m.ArchivedActivitiesComponent
          ),
      },
      {
        path: 'payment-search',
        loadComponent: () =>
          import('./components/organization/activities/payment-search/payment-search.component').then(
            m => m.PaymentSearchComponent
          ),
      },
      {
        path: 'activities/:id',
        // Nearly every visit opens an activity, so its chunk (charts, products, participants) is fetched in the background
        data: { preload: true },
        loadChildren: () => import('./components/organization/activities/activity/activity.routes').then(m => m.ACTIVITY_ROUTES),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: SelectivePreloadingStrategy })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
